/**
 * Storage abstraction for Mastodon credentials and settings.
 * Uses localStorage but can be swapped for other backends.
 */

const STORAGE_KEYS = {
	INSTANCE_DOMAIN: "instanceDomain",
	CLIENT_ID: "client_id",
	CLIENT_SECRET: "client_secret",
	AUTH_CODE: "auth_code",
	ACCESS_TOKEN: "access_token",
	MY_ID: "myId",
} as const;

export interface StoredCredentials {
	instanceDomain: string;
	clientId: string;
	clientSecret: string;
	accessToken: string;
	userId?: string;
}

class AuthStorage {
	get instanceDomain(): string | null {
		return localStorage.getItem(STORAGE_KEYS.INSTANCE_DOMAIN);
	}

	set instanceDomain(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.INSTANCE_DOMAIN, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.INSTANCE_DOMAIN);
		}
	}

	get clientId(): string | null {
		return localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
	}

	set clientId(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.CLIENT_ID, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
		}
	}

	get clientSecret(): string | null {
		return localStorage.getItem(STORAGE_KEYS.CLIENT_SECRET);
	}

	set clientSecret(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.CLIENT_SECRET, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.CLIENT_SECRET);
		}
	}

	get authCode(): string | null {
		return localStorage.getItem(STORAGE_KEYS.AUTH_CODE);
	}

	set authCode(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.AUTH_CODE, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.AUTH_CODE);
		}
	}

	get accessToken(): string | null {
		return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
	}

	set accessToken(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
		}
	}

	get userId(): string | null {
		return localStorage.getItem(STORAGE_KEYS.MY_ID);
	}

	set userId(value: string | null) {
		if (value) {
			localStorage.setItem(STORAGE_KEYS.MY_ID, value);
		} else {
			localStorage.removeItem(STORAGE_KEYS.MY_ID);
		}
	}

	/**
	 * Check if we have all credentials needed to make authenticated API calls
	 */
	isAuthenticated(): boolean {
		return !!(this.instanceDomain && this.accessToken);
	}

	/**
	 * Check if OAuth app registration is complete
	 */
	hasAppCredentials(): boolean {
		return !!(this.instanceDomain && this.clientId && this.clientSecret);
	}

	/**
	 * Get all stored credentials
	 */
	getCredentials(): StoredCredentials | null {
		if (!this.isAuthenticated()) {
			return null;
		}
		return {
			instanceDomain: this.instanceDomain!,
			clientId: this.clientId!,
			clientSecret: this.clientSecret!,
			accessToken: this.accessToken!,
			userId: this.userId ?? undefined,
		};
	}

	/**
	 * Store all credentials at once
	 */
	setCredentials(creds: Partial<StoredCredentials>): void {
		if (creds.instanceDomain !== undefined) this.instanceDomain = creds.instanceDomain;
		if (creds.clientId !== undefined) this.clientId = creds.clientId;
		if (creds.clientSecret !== undefined) this.clientSecret = creds.clientSecret;
		if (creds.accessToken !== undefined) this.accessToken = creds.accessToken;
		if (creds.userId !== undefined) this.userId = creds.userId ?? null;
	}

	/**
	 * Clear all stored auth data
	 */
	clear(): void {
		Object.values(STORAGE_KEYS).forEach((key) => {
			localStorage.removeItem(key);
		});
	}
}

/** Singleton storage instance */
export const authStorage = new AuthStorage();
